/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useRef } from 'react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from '@web-stories-wp/react';
import styled, { keyframes } from 'styled-components';
import { __ } from '@web-stories-wp/i18n';
import {
  Text,
  THEME_CONSTANTS,
  useSnackbar,
} from '@web-stories-wp/design-system';
import { Pane } from '../shared';
import { useAPI } from '../../../../app/api';
import paneId from './paneId';
import {
  MediaGalleryLoadingPill,
  SearchInputContainer,
} from '../media/common/styles';
import { SearchInput } from '../../common';
import TemplatesListByFolder from '@components/templatesListByFolder/TemplatesListByFolder.jsx';
import { useStudioContext } from '@hooks/studioContext';
import { AssetTypes } from '@src/hooks/studioContext/CMSParams';
import OptionsListBackButton from '@src/components/optionsList/optionsListBackButton/optionsListBackButton';
import { useUser } from '@hooks';
import canManageStudioTemplates from '@src/utils/canManageStudioTemplates';
import OptionsList from '@src/components/optionsList/optionsList.jsx';
import TemplateActionMenu from './templateActionMenu';
import {
  TemplateDeleteDialog,
  TemplateMoveDialog,
  TemplateTitleDialog,
} from './templateManagementDialogs';
import SaveCurrentPageAsTemplateButton from './saveCurrentPageAsTemplateButton';

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colors.fg.secondary};
`;

export const StyledPane = styled(Pane)`
  height: 100%;
  padding: 0;
  padding-top: 12px;
  overflow: hidden;
`;

export const PaneInner = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const FolderContents = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  > .optionsList {
    flex-shrink: 0;
    max-height: ${({ $hasTemplates }) => ($hasTemplates ? '50%' : '100%')};
  }
`;

const showLoading = keyframes`
  to { opacity: 1; }
`;

const LoadingPill = styled(MediaGalleryLoadingPill)`
  opacity: 0;
  animation: ${showLoading} 0s 200ms forwards;
  pointer-events: none;
  z-index: 1;
`;

const FolderLoadError = styled.div`
  margin: 16px 20px;
  color: #ff8b8b;
  font-size: 13px;
`;

const RetryButton = styled.button`
  margin-left: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #76a0f0;
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
`;

const TemplateSearchInputContainer = styled(SearchInputContainer)`
  form > div {
    height: 40px;
  }
`;

const DEFAULT_PAGE_NUMBER = 1;
const TEMPLATE_PAGE_SIZE = 16;
const TEMPLATE_ACTIONS = {
  EDIT_TITLE: 'edit-title',
  MOVE: 'move',
  TOGGLE_PIN: 'toggle-pin',
  DELETE: 'delete',
};

const isNotFound = (error) =>
  error?.response?.status === 404 || error?.status === 404;

const isConcurrencyError = (error) =>
  error?.response?.status === 409 || error?.status === 409;

const getFolderPathLabel = (folderPath = []) =>
  folderPath.map(({ name }) => name).join(' / ');

const pathsMatch = (left = [], right = []) =>
  left.length === right.length &&
  left.every(
    (segment, index) =>
      segment.id === right[index]?.id && segment.name === right[index]?.name
  );

function updateUnpinnedDisplayOrder(templates) {
  let nextDisplayOrder = 0;

  return templates.map((template) => {
    if (template.isPinned) {
      return template;
    }

    const displayOrder = nextDisplayOrder;
    nextDisplayOrder += 1;
    return { ...template, displayOrder };
  });
}

function PageTemplatesWithFoldersPane(props) {
  const {
    actions: {
      getStudioTemplateFolders,
      getStudioTemplateFolder,
      getStudioTemplateFolderDestinations,
      getCustomPageTemplates,
      updatePageTemplateMetadata,
      updatePageTemplateOrder,
      updatePageTemplatePin,
      movePageTemplate,
      deletePageTemplate,
    },
  } = useAPI();
  const user = useUser();
  const { showSnackbar } = useSnackbar();
  const studioContext = useStudioContext();
  const {
    assetType,
    contentBankAssetType,
    cardsAssetType,
    templateAssetTypes,
  } = studioContext;
  const templateAssetType = Array.isArray(templateAssetTypes)
    ? templateAssetTypes[0]
    : null;
  const finalAssetType =
    contentBankAssetType ||
    cardsAssetType ||
    (assetType === AssetTypes.Template ? templateAssetType : assetType);
  const canManageTemplates = canManageStudioTemplates(user);

  const [folderPath, setFolderPath] = useState([]);
  const [searchTemplateQuery, setSearchTemplateQuery] = useState('');
  const [foldersList, setFoldersList] = useState([]);
  const [templatesList, setTemplatesList] = useState([]);
  const [templateListKey, setTemplateListKey] = useState('');
  const [nextTemplatesPage, setNextTemplatesPage] = useState(false);
  const [isFolderRequestPending, setIsFolderRequestPending] = useState(false);
  const [isTemplatesRequestPending, setIsTemplatesRequestPending] =
    useState(false);
  const [folderLoadError, setFolderLoadError] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState(false);
  const [isMutationPending, setIsMutationPending] = useState(false);
  const [activeDialog, setActiveDialog] = useState(null);
  const [destinationFolders, setDestinationFolders] = useState([]);
  const [isLoadingDestinations, setIsLoadingDestinations] = useState(false);
  const [destinationLoadError, setDestinationLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const requestIdRef = useRef(0);
  const templateControllerRef = useRef(null);
  const templateLoadingRef = useRef(false);
  const searchRef = useRef();
  const currentFolderId = folderPath[folderPath.length - 1]?.id || null;
  const isFolderSelected = Boolean(currentFolderId);
  const isSearching = Boolean(searchTemplateQuery);
  const canReorderTemplates =
    canManageTemplates &&
    isFolderSelected &&
    !isSearching &&
    !isMutationPending &&
    !isFolderRequestPending;

  const refreshVisibleData = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  const navigateToPath = useCallback((nextPath) => {
    setFolderPath(nextPath);
    setSearchTemplateQuery('');
    searchRef.current?.setValue('');
  }, []);

  const findClosestExistingAncestor = useCallback(
    async (path, signal) => {
      for (let index = path.length - 2; index >= 0; index -= 1) {
        try {
          const ancestor = await getStudioTemplateFolder(
            path[index].id,
            signal
          );
          return ancestor;
        } catch (error) {
          if (!isNotFound(error)) {
            throw error;
          }
        }
      }

      return null;
    },
    [getStudioTemplateFolder]
  );

  useEffect(() => {
    if (!finalAssetType) return undefined;

    const requestId = ++requestIdRef.current;
    const folderController = new AbortController();
    const templateController = new AbortController();
    templateControllerRef.current = templateController;
    templateLoadingRef.current = false;
    let isCurrent = true;
    let isResolvingCurrentFolder = Boolean(currentFolderId);

    setIsFolderRequestPending(true);
    setIsTemplatesRequestPending(false);
    setFolderLoadError(false);
    setTemplateLoadError(false);
    setNextTemplatesPage(false);

    const recoverDeletedFolder = async () => {
      const ancestor = await findClosestExistingAncestor(
        folderPath,
        folderController.signal
      );
      if (!isCurrent || requestId !== requestIdRef.current) {
        return true;
      }

      setFolderPath(ancestor?.path || []);
      return true;
    };

    const load = async () => {
      try {
        if (currentFolderId) {
          let currentFolder;
          try {
            currentFolder = await getStudioTemplateFolder(
              currentFolderId,
              folderController.signal
            );
          } catch (error) {
            if (!isNotFound(error)) {
              throw error;
            }
            await recoverDeletedFolder();
            return;
          }
          isResolvingCurrentFolder = false;

          if (!isCurrent || requestId !== requestIdRef.current) {
            return;
          }

          if (
            currentFolder?.path &&
            !pathsMatch(currentFolder.path, folderPath)
          ) {
            setFolderPath(currentFolder.path);
            return;
          }
        }

        const folderPromise = isSearching
          ? Promise.resolve({ folders: [] })
          : getStudioTemplateFolders(
              {
                ...(currentFolderId ? { parentFolderId: currentFolderId } : {}),
                assetType: finalAssetType,
              },
              folderController.signal
            );

        if (currentFolderId || isSearching) {
          setIsTemplatesRequestPending(true);
          const results = await Promise.allSettled([
            folderPromise,
            getCustomPageTemplates(
              DEFAULT_PAGE_NUMBER,
              TEMPLATE_PAGE_SIZE,
              '',
              searchTemplateQuery,
              templateController.signal,
              finalAssetType,
              currentFolderId,
              Boolean(currentFolderId && isSearching)
            ),
          ]);

          if (!isCurrent || requestId !== requestIdRef.current) {
            return;
          }

          const [folderResult, templateResult] = results;
          if (folderResult.status === 'rejected') {
            if (currentFolderId && isNotFound(folderResult.reason)) {
              await recoverDeletedFolder();
              return;
            }
            setFolderLoadError(true);
            setFoldersList([]);
          } else {
            setFoldersList(folderResult.value.folders || []);
          }

          if (templateResult.status === 'rejected') {
            if (currentFolderId && isNotFound(templateResult.reason)) {
              await recoverDeletedFolder();
              return;
            }
            setTemplateLoadError(true);
            setTemplatesList([]);
          } else {
            setTemplatesList(templateResult.value.templates || []);
            // Reset the grid's scroll position when the new destination is ready.
            setTemplateListKey(
              JSON.stringify([
                finalAssetType,
                currentFolderId,
                searchTemplateQuery,
              ])
            );
            setNextTemplatesPage(
              templateResult.value.hasMore ? DEFAULT_PAGE_NUMBER + 1 : false
            );
          }
        } else {
          const folderResult = await folderPromise;
          if (!isCurrent || requestId !== requestIdRef.current) {
            return;
          }
          setFoldersList(folderResult.folders || []);
          setTemplatesList([]);
        }
      } catch (error) {
        if (!isCurrent || requestId !== requestIdRef.current) {
          return;
        }

        setFoldersList([]);
        setTemplatesList([]);

        if (currentFolderId && isNotFound(error)) {
          try {
            await recoverDeletedFolder();
          } catch (recoveryError) {
            if (isCurrent && requestId === requestIdRef.current) {
              setFolderLoadError(true);
              setTemplateLoadError(true);
            }
          }
          return;
        }

        if (isResolvingCurrentFolder) {
          setFolderLoadError(true);
        } else if (isSearching || currentFolderId) {
          setTemplateLoadError(true);
        } else {
          setFolderLoadError(true);
        }
      } finally {
        if (isCurrent && requestId === requestIdRef.current) {
          setIsFolderRequestPending(false);
          setIsTemplatesRequestPending(false);
        }
      }
    };

    load();

    return () => {
      isCurrent = false;
      folderController.abort();
      templateController.abort();
      if (templateControllerRef.current === templateController) {
        templateControllerRef.current = null;
      }
    };
  }, [
    currentFolderId,
    finalAssetType,
    findClosestExistingAncestor,
    folderPath,
    getCustomPageTemplates,
    getStudioTemplateFolder,
    getStudioTemplateFolders,
    isSearching,
    reloadKey,
    searchTemplateQuery,
  ]);

  const fetchTemplates = useCallback(async () => {
    if (
      isFolderRequestPending ||
      !nextTemplatesPage ||
      templateLoadingRef.current
    ) {
      return;
    }

    const requestId = requestIdRef.current;
    const controller = templateControllerRef.current;
    if (!controller || controller.signal.aborted) {
      return;
    }

    const page = nextTemplatesPage;
    templateLoadingRef.current = true;
    setIsTemplatesRequestPending(true);

    try {
      const result = await getCustomPageTemplates(
        page,
        TEMPLATE_PAGE_SIZE,
        '',
        searchTemplateQuery,
        controller.signal,
        finalAssetType,
        currentFolderId,
        Boolean(currentFolderId && isSearching)
      );

      if (requestId !== requestIdRef.current || controller.signal.aborted) {
        return;
      }

      setTemplatesList((current) => {
        const existingIds = new Set(
          current.map((template) => template.templateId)
        );
        return [
          ...current,
          ...result.templates.filter(
            (template) => !existingIds.has(template.templateId)
          ),
        ];
      });
      setNextTemplatesPage(result.hasMore ? page + 1 : false);
    } catch (error) {
      if (requestId === requestIdRef.current && !controller.signal.aborted) {
        setTemplateLoadError(true);
        setNextTemplatesPage(false);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        templateLoadingRef.current = false;
        setIsTemplatesRequestPending(false);
      }
    }
  }, [
    currentFolderId,
    finalAssetType,
    getCustomPageTemplates,
    isFolderRequestPending,
    isSearching,
    nextTemplatesPage,
    searchTemplateQuery,
  ]);

  const handleSearch = useCallback((searchText = '') => {
    setSearchTemplateQuery(searchText);
  }, []);

  const handleChooseFolder = useCallback(
    (folderId) => {
      const folder = foldersList.find((item) => item.id === folderId);
      if (folder?.path) {
        navigateToPath(folder.path);
      }
    },
    [foldersList, navigateToPath]
  );

  const closeActiveDialog = useCallback(() => {
    if (!isMutationPending) {
      setActiveDialog(null);
    }
  }, [isMutationPending]);

  useEffect(() => {
    if (activeDialog?.type !== TEMPLATE_ACTIONS.MOVE) {
      return undefined;
    }

    const controller = new AbortController();
    let isCurrent = true;
    setIsLoadingDestinations(true);
    setDestinationLoadError(false);

    getStudioTemplateFolderDestinations(controller.signal)
      .then(({ folders }) => {
        if (isCurrent) {
          setDestinationFolders(folders || []);
        }
      })
      .catch(() => {
        if (isCurrent && !controller.signal.aborted) {
          setDestinationFolders([]);
          setDestinationLoadError(true);
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoadingDestinations(false);
        }
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [activeDialog?.type, getStudioTemplateFolderDestinations]);

  const runMutation = useCallback(
    async (mutation, successMessage) => {
      if (isMutationPending) {
        return;
      }

      setIsMutationPending(true);
      try {
        await mutation();
        showSnackbar({ message: successMessage, dismissable: true });
        setActiveDialog(null);
        refreshVisibleData();
      } catch (error) {
        showSnackbar({
          message: isConcurrencyError(error)
            ? 'Template changed elsewhere. Refreshed; try again.'
            : 'Template update failed. Please try again.',
          dismissable: true,
        });
        if (isConcurrencyError(error)) {
          setActiveDialog(null);
          refreshVisibleData();
        }
      } finally {
        setIsMutationPending(false);
      }
    },
    [isMutationPending, refreshVisibleData, showSnackbar]
  );

  const getTemplateMenuItems = useCallback(
    (template) => {
      const items = [
        { label: 'Edit Title', value: TEMPLATE_ACTIONS.EDIT_TITLE },
        { label: 'Move to Folder...', value: TEMPLATE_ACTIONS.MOVE },
      ];

      if (isFolderSelected) {
        items.push({
          label: template?.isPinned ? 'Unpin' : 'Pin',
          value: TEMPLATE_ACTIONS.TOGGLE_PIN,
        });
      }

      items.push({ label: 'Delete', value: TEMPLATE_ACTIONS.DELETE });
      return items;
    },
    [isFolderSelected]
  );

  const handlePinTemplate = useCallback(
    (template) => {
      if (!isFolderSelected) {
        return;
      }

      const nextIsPinned = !template.isPinned;
      runMutation(
        () =>
          updatePageTemplatePin(template.templateId, {
            isPinned: nextIsPinned,
            assetType: finalAssetType,
          }),
        nextIsPinned ? 'Template pinned.' : 'Template unpinned.'
      );
    },
    [finalAssetType, isFolderSelected, runMutation, updatePageTemplatePin]
  );

  const handleTemplateAction = useCallback(
    (action, template) => {
      if (isMutationPending) {
        return;
      }
      if (action === TEMPLATE_ACTIONS.TOGGLE_PIN) {
        handlePinTemplate(template);
        return;
      }
      setActiveDialog({ type: action, template });
    },
    [handlePinTemplate, isMutationPending]
  );

  const handleTemplateReorder = useCallback(
    async ({ template, fromIndex, toIndex }) => {
      if (
        !canReorderTemplates ||
        !template ||
        template.isPinned ||
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        isMutationPending
      ) {
        return;
      }

      const previousTemplates = templatesList;
      const requestId = requestIdRef.current;
      const reorderedTemplates = [...templatesList];
      const [movedTemplate] = reorderedTemplates.splice(fromIndex, 1);
      reorderedTemplates.splice(toIndex, 0, movedTemplate);
      const orderedTemplates = updateUnpinnedDisplayOrder(reorderedTemplates);
      const displayOrder =
        orderedTemplates.find((item) => item.templateId === template.templateId)
          ?.displayOrder ?? 0;

      setTemplatesList(orderedTemplates);
      setIsMutationPending(true);
      try {
        await updatePageTemplateOrder(template.templateId, {
          displayOrder,
          assetType: finalAssetType,
        });
        refreshVisibleData();
      } catch (error) {
        if (requestId === requestIdRef.current) {
          setTemplatesList(previousTemplates);
        }
        showSnackbar({
          message: isConcurrencyError(error)
            ? 'Template changed elsewhere. Refreshed; try again.'
            : 'Template order update failed. Please try again.',
          dismissable: true,
        });
        refreshVisibleData();
      } finally {
        setIsMutationPending(false);
      }
    },
    [
      canReorderTemplates,
      finalAssetType,
      isMutationPending,
      refreshVisibleData,
      showSnackbar,
      templatesList,
      updatePageTemplateOrder,
    ]
  );

  const renderTemplateActions = useCallback(
    (template) =>
      canManageTemplates ? (
        <TemplateActionMenu
          ariaLabel={`Template actions for ${template.title || 'template'}`}
          items={getTemplateMenuItems(template)}
          onSelect={(action) => handleTemplateAction(action, template)}
        />
      ) : null,
    [canManageTemplates, getTemplateMenuItems, handleTemplateAction]
  );

  const handleTemplateTitleSave = useCallback(
    (title) => {
      const { template } = activeDialog || {};
      if (!template) {
        return;
      }

      runMutation(
        () =>
          updatePageTemplateMetadata(template.templateId, {
            title,
            assetType: finalAssetType,
          }),
        'Template title updated.'
      );
    },
    [activeDialog, finalAssetType, runMutation, updatePageTemplateMetadata]
  );

  const handleTemplateMoveSave = useCallback(
    (destinationFolderId) => {
      const { template } = activeDialog || {};
      if (!template || !destinationFolderId) {
        return;
      }

      runMutation(
        () =>
          movePageTemplate(template.templateId, {
            destinationFolderId,
            concurrencyStamp: template.concurrencyStamp,
            assetType: finalAssetType,
          }),
        'Template moved.'
      );
    },
    [activeDialog, finalAssetType, movePageTemplate, runMutation]
  );

  const handleTemplateDelete = useCallback(() => {
    const { template } = activeDialog || {};
    if (!template) {
      return;
    }

    runMutation(
      () => deletePageTemplate(template.templateId, finalAssetType),
      'Template deleted.'
    );
  }, [activeDialog, deletePageTemplate, finalAssetType, runMutation]);

  const isTemplateListVisible =
    templatesList.length > 0 ||
    (!isFolderRequestPending && (isFolderSelected || isSearching));
  const folderOptions = useMemo(
    () =>
      foldersList.map((folder) => ({
        key: folder.id,
        label: getFolderPathLabel(folder.path),
      })),
    [foldersList]
  );
  const shouldDisplayNoTemplateFoundMessage =
    !isFolderRequestPending &&
    !isTemplatesRequestPending &&
    !templateLoadError &&
    templatesList.length === 0 &&
    (isSearching || foldersList.length === 0);

  return (
    <StyledPane id={paneId} {...props}>
      <PaneInner>
        {canManageTemplates && (
          <SaveCurrentPageAsTemplateButton
            assetType={finalAssetType}
            initialFolderId={currentFolderId || ''}
            onTemplateSaved={refreshVisibleData}
          />
        )}
        {isFolderSelected && (
          <nav aria-label="Template folder path">
            <OptionsListBackButton
              as="button"
              type="button"
              onClick={() => navigateToPath(folderPath.slice(0, -1))}
              aria-label={
                folderPath.length > 1
                  ? `Back to ${folderPath[folderPath.length - 2].name}`
                  : 'Back to all folders'
              }
            >
              {getFolderPathLabel(folderPath)}
            </OptionsListBackButton>
          </nav>
        )}
        <TemplateSearchInputContainer marginTop={canManageTemplates ? 16 : 12}>
          <SearchInput
            incremental
            initialValue=""
            onSearch={handleSearch}
            placeholder={__('Search', 'web-stories')}
            ref={searchRef}
          />
        </TemplateSearchInputContainer>
        <FolderContents
          aria-busy={isFolderRequestPending}
          inert={isFolderRequestPending ? '' : undefined}
          $hasTemplates={isTemplateListVisible}
        >
          {folderOptions.length > 0 && (
            <OptionsList
              setChosenOption={handleChooseFolder}
              options={folderOptions}
              shouldDisplayNoOptionFoundMessage={false}
              shouldDisplayOptionsList
              disableAllOptions
            />
          )}
          {folderLoadError && (
            <FolderLoadError role="alert">
              Unable to load template folders.
              <RetryButton type="button" onClick={refreshVisibleData}>
                Retry
              </RetryButton>
            </FolderLoadError>
          )}
          {templateLoadError && (
            <FolderLoadError role="alert">
              Unable to load templates.
              <RetryButton type="button" onClick={refreshVisibleData}>
                Retry
              </RetryButton>
            </FolderLoadError>
          )}
          {isTemplateListVisible && !templateLoadError && (
            <TemplatesListByFolder
              key={templateListKey}
              templates={templatesList}
              loadTemplates={fetchTemplates}
              canReorderTemplates={canReorderTemplates}
              onReorderTemplate={handleTemplateReorder}
              shouldDisplayNoTemplateFoundMessage={
                shouldDisplayNoTemplateFoundMessage
              }
              renderTemplateActions={
                canManageTemplates ? renderTemplateActions : undefined
              }
            />
          )}
        </FolderContents>
        {isFolderRequestPending || isTemplatesRequestPending ? (
          <LoadingPill data-testid="loading-pill">
            <StyledText
              forwardedAs="span"
              size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}
            >
              {__('Loading…', 'web-stories')}
            </StyledText>
          </LoadingPill>
        ) : null}
        <TemplateTitleDialog
          isOpen={activeDialog?.type === TEMPLATE_ACTIONS.EDIT_TITLE}
          isSaving={isMutationPending}
          template={activeDialog?.template}
          onClose={closeActiveDialog}
          onSave={handleTemplateTitleSave}
        />
        <TemplateMoveDialog
          folders={destinationFolders}
          isLoading={isLoadingDestinations}
          loadError={destinationLoadError}
          isOpen={activeDialog?.type === TEMPLATE_ACTIONS.MOVE}
          isSaving={isMutationPending}
          template={activeDialog?.template}
          onClose={closeActiveDialog}
          onSave={handleTemplateMoveSave}
        />
        <TemplateDeleteDialog
          isOpen={activeDialog?.type === TEMPLATE_ACTIONS.DELETE}
          isSaving={isMutationPending}
          template={activeDialog?.template}
          onClose={closeActiveDialog}
          onDelete={handleTemplateDelete}
        />
      </PaneInner>
    </StyledPane>
  );
}

export default PageTemplatesWithFoldersPane;
